import { Select, Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { EvaluationFacultySummary } from '@/module/Evaluation/EvaluationFacultySummary';
import { EvaluationHeader } from '@/module/Evaluation/components/EvaluationHeader';
import { getEvaluationOptions } from '@/module/Evaluation/model/EvaluationQuery';
import { getAccessibleFacultiesOptions } from '@/module/Faculty/model/FacultyQuery';

import { Content, NoContentPlaceholder } from '@/core/components';

import { useCurrentUser } from '@feature/security';

const evaluationSummarySchema = z.object({
  facultyId: z.coerce.string().nullish(),
});

export const Route = createFileRoute('/_auth/evaluation/_layout/$evaluationId/faculty-summary-results')({
  component: RouteComponent,
  validateSearch: evaluationSummarySchema,
  loader: async ({ params: { evaluationId }, context: { queryClient } }) => {
    const evaluation = await queryClient.ensureQueryData(getEvaluationOptions(evaluationId));

    return {
      evaluation,
      crumb: evaluation.title,
    };
  },
});

function RouteComponent() {
  const { evaluationId } = Route.useParams();
  const { facultyId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: evaluation } = useSuspenseQuery(getEvaluationOptions(evaluationId));
  const { data: faculties } = useSuspenseQuery(getAccessibleFacultiesOptions());

  const { currentRole } = useCurrentUser();
  const hasAccess = ['FACULTY_ADMIN', 'UNIVERSITY_ADMIN'].indexOf(currentRole) !== -1;

  const onFacultyChange = (newValue: string | null) => {
    navigate({
      to: '.',
      search: prev => ({ ...prev, facultyId: newValue }),
    });
  };

  if (!hasAccess) {
    return (
      <NoContentPlaceholder title="Nu aveți acces la acest ecran cu acest rol. Asigurați-vă că folosiți un rol de decan" />
    );
  }

  return (
    <Stack>
      <EvaluationHeader
        hideSummary
        evaluation={evaluation}
      />
      <Content type="full">
        <Stack>
          <Select
            flex={1}
            label="Facultate"
            placeholder="Facultate"
            searchable
            data={faculties.map(fac => ({ label: fac.name, value: `${fac.id}` }))}
            value={facultyId}
            onChange={onFacultyChange}
          />
          {facultyId != null ? (
            <EvaluationFacultySummary
              evaluationId={evaluationId}
              facultyId={Number(facultyId)}
            />
          ) : (
            <NoContentPlaceholder title="Nu aveți acces la acest ecran cu acest rol. Asigurați-vă că folosiți un rol de decan" />
          )}
        </Stack>
      </Content>
    </Stack>
  );
}
